import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Box, Container, IconButton, Stack } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid-pro";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { DEFAULT_ROWS_PER_PAGE } from "api/constants";
import { useDetectorGroups } from "api/services/useDetectorGroups";
import { AddIcon, DeleteIcon, EditIcon } from "assets/icons";
import { ErrorView } from "components/common/ErrorView";
import { getErrorTypeFromStatus } from "components/common/ErrorView/utils";
import { SubNavBar } from "components/common/SubNavBar";
import { DetectorGroup } from "components/createDetector/CreateDetectorModal";
import { useDataGridController } from "hooks/useDataGridController/useDataGridController";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { StyledDataGrid } from "new-components/dataGrid/StyledDataGrid";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";
import { extractPaginateFrom } from "utils/utilityFunctions";

import {
  DetectorGroupFormModal,
  DetectorGroupModalData,
  ModalAction,
} from "./DetectorGroupFormModal/DetectorGroupFormModal";

export function DetectorGroupsPage() {
  const [paginateFrom, setPaginateFrom] = useState<string>();
  const { page, pageSize, sortModel, onPageChange, onPageSizeChange, onSortModelChange } = useDataGridController();
  const navigate = useNavigate();

  const {
    // get operations
    detectorGroups,
    isLoadingDetectorGroups,
    detectorGroupsError,

    // delete operations
    deleteDetectorGroup,
    isDeletingDetectorGroup,
  } = useDetectorGroups({
    getOptions: {
      params: {
        paginate_from: paginateFrom,
        page_size: pageSize,
        ordering: sortModel.map((model) => (model.sort === "asc" ? model.field : `-${model.field}`)).join(","),
      },
    },
  });

  const handlePageChange = (newPage: number) => {
    onPageChange(newPage);

    if (!detectorGroups) {
      return;
    }

    if (newPage > page) {
      const paginateFrom = extractPaginateFrom(detectorGroups.next);

      if (paginateFrom) {
        setPaginateFrom(paginateFrom);
      }
    }

    if (newPage < page) {
      const paginateFrom = extractPaginateFrom(detectorGroups.previous);

      if (paginateFrom) {
        setPaginateFrom(paginateFrom);
      }
    }
  };

  const [modalAction, setModalAction] = useState<ModalAction<DetectorGroupModalData> | null>(null);
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);

  const handleViewDetectorsOfGroup = (detectorGroup: DetectorGroup) => {
    navigate(`/reef/?group_id=${detectorGroup.id}&group_name=${detectorGroup.name}`);
  };

  const errorType = detectorGroupsError ? getErrorTypeFromStatus(detectorGroupsError.status) : null;

  if (errorType) {
    return <ErrorView error={errorType} />;
  }

  return (
    <Box width="100%" height="100%">
      <SubNavBar>
        <Link to="/reef" className="no-underline">
          <IconButton aria-label="back" className="p-1 ml-1">
            <ArrowBackIosNewIcon className="text-white" />
          </IconButton>
        </Link>
        <Typography textTransform="uppercase" className="self-center" variant="subtitle" fontWeight="semibold" noWrap>
          all detectors / manage groups
        </Typography>
      </SubNavBar>

      <Container sx={{ height: "calc(100% - 100px)" }}>
        <Box display="flex" alignItems="center" justifyContent="end" height="50px">
          <GroundlightButton
            endIcon={AddIcon}
            onClick={() => {
              setModalAction({
                action: "create",
              });
            }}
          >
            Create new group
          </GroundlightButton>
        </Box>
        <StyledDataGrid
          onRowClick={(params: GridRowParams<DetectorGroup>) => {
            handleViewDetectorsOfGroup(params.row);
          }}
          columns={[
            {
              flex: 1,
              field: "name",
              headerName: "Group name",
              headerAlign: "center",
              renderCell: (params) => <Typography>{params.row.name}</Typography>,
            },
            {
              flex: 1,
              field: "detectors_count",
              headerName: "Detectors in group",
              headerAlign: "center",
              align: "center",
              renderCell: (params) => <Typography>{params.row.detectors_count}</Typography>,
            },
            {
              flex: 1,
              field: "actions",
              headerAlign: "center",
              headerName: "Actions",
              maxWidth: 290,
              sortable: false,
              renderCell: (params) => (
                <Stack gap="10px" direction="row" alignItems="center">
                  <GroundlightButton
                    sx={{
                      height: "30px",
                    }}
                    onClick={() => {
                      handleViewDetectorsOfGroup(params.row);
                    }}
                  >
                    View detectors
                  </GroundlightButton>

                  {params.row.is_editable && (
                    <IconButton
                      aria-label="edit"
                      onClick={(event) => {
                        event.stopPropagation();
                        setModalAction({
                          action: "update",
                          data: {
                            id: params.row.id,
                            name: params.row.name,
                          },
                        });
                      }}
                    >
                      <EditIcon color="#919599" />
                    </IconButton>
                  )}

                  {params.row.is_deletable && (
                    <IconButton
                      aria-label="delete"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDeleteGroupId(params.row.id);
                      }}
                    >
                      <DeleteIcon color="#919599" />
                    </IconButton>
                  )}
                </Stack>
              ),
            },
          ]}
          rows={detectorGroups?.results ?? []}
          rowCount={detectorGroups?.count ?? 0}
          loading={isLoadingDetectorGroups}
          page={page}
          pageSize={pageSize}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE}
          onPageChange={handlePageChange}
          onPageSizeChange={onPageSizeChange}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          pagination
          paginationMode="server"
          filterMode="server"
          sortingMode="server"
          disableColumnMenu
        />
      </Container>

      <DetectorGroupFormModal action={modalAction} onClose={() => setModalAction(null)} />

      <Modal
        title="Delete group"
        open={deleteGroupId !== null}
        size="small"
        titleAlignment="left"
        onClose={() => {
          setDeleteGroupId(null);
        }}
        onCancel={() => {
          setDeleteGroupId(null);
        }}
        confirmationButtonTitle="Accept"
        isConfirmationLoading={isDeletingDetectorGroup}
        isConfirmationDisabled={isDeletingDetectorGroup}
        onConfirmation={() => {
          if (deleteGroupId) {
            deleteDetectorGroup(deleteGroupId, {
              onSuccess: () => {
                setDeleteGroupId(null);
              },
            });
          }
        }}
        body={
          <Typography textAlign="center">
            Deleting a detector group will not delete the contents. Any detectors contained within the group will be
            moved to the default group.
          </Typography>
        }
      />
    </Box>
  );
}
